import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { getCategoriesFunc } from "../../../store/custom-actions/categories-actions/GetCategoriesSlicer";
import { deleteCategoryFunc } from "../../../store/custom-actions/categories-actions/DeleteCategoriesSlicer";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";

import { getLocalizedTranslation } from "../../../utils/localization";
import { useTranslation } from "react-i18next";
// Utilities & Helpers
import { handleDeleteEntity } from "../../../utils/deleteHelper";
import checkPermissions from "../../global-functions/checkPermissionsFunc";

// UI Components
import SearchBox from "../../components/ui/SearchBox";
import Table from "../../components/ui/Table";
import Pagination from "../../components/ui/Pagination";

function CategoriesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { loading: getCategoriesLoading, data: categoriesData = [] } =
    useSelector((state) => state.getCategoriesSlicer);
  const { deleteCategoriesLoading } = useSelector(
    (state) => state.deleteCategorySlicer
  );
  const { loading: switchLangLoading, data: languageData } = useSelector(
    (state) => state.getLanguageSettingsSlicer
  );

  const [categoriesList, setCategoriesList] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const sort = 20;
  const activePag = useRef(0);

  // Get all categories
  const getCategoriesData = async (query = "") => {
    try {
      await dispatch(getCategoriesFunc(query));
    } catch (error) {
      console.log("Error fetching categories:", error);
    }
  };

  // Handle URL query for parent_id
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const parentId = query.get("parent_id");

    if (parentId) {
      getCategoriesData(`?parent_id=${parentId}`);
    } else {
      getCategoriesData();
    }
  }, [location.search]);

  // Update category list + pagination data on categories change
  useEffect(() => {
    if (Array.isArray(categoriesData)) {
      setCategoriesList(categoriesData);
      setPaginatedData(
        categoriesData.slice(
          activePag.current * sort,
          (activePag.current + 1) * sort
        )
      );
    }
  }, [categoriesData]);

  // Pagination click handler
  const onClick = (i) => {
    activePag.current = i;
    setPaginatedData(
      categoriesList.slice(
        activePag.current * sort,
        (activePag.current + 1) * sort
      )
    );
  };

  // Search handler
  const search = () => {
    const safeSearchValue = (searchValue || "").toLocaleLowerCase().trim();

    if (safeSearchValue) {
      const searchResult = categoriesList.filter(
        (cat) =>
          typeof cat.name === "string" &&
          cat.name.toLocaleLowerCase().includes(safeSearchValue)
      );
      setPaginatedData(searchResult);
    } else {
      setPaginatedData(
        categoriesList.slice(
          activePag.current * sort,
          (activePag.current + 1) * sort
        )
      );
    }
  };

  // Navigate to children using query param
  const getChildren = (parentId) => {
    navigate(`?parent_id=${parentId}`);
  };

  // Handle deletion
  const handleDeleteItem = (id, entityName = "") => {
    handleDeleteEntity({
      id,
      entityName,
      deleteFunc: deleteCategoryFunc,
      dispatch,
      refetchFunc: getCategoriesFunc,
      t,
    });
  };

  return (
    <>
      <PageTitle motherMenu={"Dashboard"} activeMenu={"Categories"} />
      <div className="categories-section relative ">
        <div className="col-12">
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <SearchBox
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onSearch={() => search()}
              />
              {checkPermissions(
                "create categories",
                <Link
                  to={"/new-category"}
                  className="btn btn-primary btn-md"
                  onClick={() => navigate(`/new-category`)}
                >
                  {t("Create")}
                </Link>
              )}
            </div>
            <div className="card-body">
              <div className="w-100 table-responsive">
                <div id="example_wrapper" className="dataTables_wrapper">
                  <Table
                    showIndex={true}
                    startIndex={activePag.current * sort}
                    columns={[
                      {
                        header: t("Category"),
                        render: (row) => {
                          const categoryNameObj = getLocalizedTranslation(
                            row.translations,
                            languageData?.defaultLang?.slug
                          );
                          return (
                            <span
                              className="text-primary fw-bold cursor-pointer"
                              onClick={() => getChildren(row.id)}
                            >
                              {categoryNameObj?.name}
                            </span>
                          );
                        },
                      },
                      {
                        header: t("Sub Categories"),
                        render: (row) =>
                          row.children && row.children.length > 0 ? (
                            <ul className="list-disc p-4">
                              {row.children.map((child, index) => (
                                <li key={child.id}>
                                  {index + 1}. {child.name}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span>{t("No Sub Category")}</span>
                          ),
                      },
                      {
                        header: t("Status"),
                        render: (row) => (
                          <span
                            className={`badge ${
                              row.active == 1 ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {row.active ? t("Active") : t("Not Active")}
                          </span>
                        ),
                      },
                    ]}
                    data={paginatedData}
                    loading={getCategoriesLoading || switchLangLoading}
                    actions={(category) =>
                      checkPermissions(
                        ["delete categories", "update categories"],
                        <>
                          {checkPermissions(
                            "update categories",
                            <Link
                              to={`/update-category/${category?.id}`}
                              className="btn btn-primary btn-xs sharp"
                            >
                              <i className="fas fa-pencil-alt"></i>
                            </Link>
                          )}
                          {checkPermissions(
                            "delete categories",
                            <Link
                              to="#"
                              className="btn btn-danger btn-xs sharp mx-1"
                              onClick={() => {
                                handleDeleteItem(category.id, category.name);
                              }}
                            >
                              <i className="fa fa-trash"></i>
                            </Link>
                          )}
                          <button
                            onClick={() => getChildren(category.id)}
                            className="btn btn-info btn-xs sharp"
                          >
                            <i className="fa fa-eye"></i>
                          </button>
                        </>
                      )
                    }
                  />
                  <Pagination
                    totalItems={categoriesList.length}
                    currentPage={activePag.current}
                    perPage={sort}
                    onPageChange={(page) => onClick(page)}
                    t={t}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoadingScreen loading={deleteCategoriesLoading} />
    </>
  );
}

export default CategoriesPage;
